/**
 * Project content. SOURCE OF TRUTH: each repository's README on GitHub
 * (https://github.com/DEADSERPENT), read 2026-10-23. Nothing here is invented;
 * numbers are quoted from the README and attributed to it. If a README changes,
 * update the entry and the `asOf` field.
 *
 * status: how mature the work is, stated conservatively.
 *   "Published"          – publicly installable or live (registry, marketplace, URL)
 *   "Working prototype"  – runs end to end; not presented as production
 *   "Core complete"      – the engine is done, listed roadmap items are not
 *   "Research prototype" – implements a method; results to be read in the repo
 *   "Ongoing research"   – an active study; results so far are shown, nothing is claimed as published
 */

export const GITHUB = "https://github.com/DEADSERPENT";

export const domains = [
  { id: "all", label: "All" },
  { id: "ml", label: "ML systems" },
  { id: "hpc", label: "GPU / HPC" },
  { id: "vision", label: "Computer vision" },
  { id: "privacy", label: "Federated & private ML" },
  { id: "tools", label: "Developer tools" },
  { id: "backend", label: "Backend" },
];

export const projects = [
  {
    slug: "pp-hfm",
    short: "PP-HFM",
    title: "PP-HFM",
    repoName: "HFL",
    domain: "privacy",
    status: "Ongoing research",
    featured: true,
    tagline:
      "Patient-private hierarchical federated learning for multimodal clinical IoT: models train across sites without moving records, with differential privacy counted per patient, not per record.",
    stack: ["Python 3.11", "PyTorch + Opacus", "NS-3", "CloudSim Plus", "ONNX Runtime"],
    pipeline: ["Window", "Encode both modalities", "Fuse", "Head-only DP", "Edge → cloud aggregate", "Calibrated prediction set"],
    repo: "https://github.com/DEADSERPENT/HFL",
    problem:
      "Hospital data cannot be centralised, and model updates can leak. Here a sample is a 10-minute window and each patient contributes about eight of them, so a textbook record-level ε answers 'was this window in training?' when the clinical question is 'was this patient in training?'.",
    approach:
      "Train across sites without moving raw records, fuse two genuinely co-recorded modalities per patient, and bound what the model reveals about any individual with patient-level differential privacy applied to the model head. The whole stack is validated end to end across network, compute, energy and deployed inference.",
    architecture: [
      { name: "Data", detail: "VitalDB (48,712 windows from 6,257 surgeries, 9 clients) as the primary corpus and WEAR (38,578 windows, 22 clients) for fast iteration. Both are open research datasets." },
      { name: "Encoders and fusion head", detail: "One encoder per modality, fused into a shared head; fusion beats both single modalities." },
      { name: "Privacy", detail: "DP-SGD with patient-level accounting (Opacus), applied to the head only." },
      { name: "Systems validation", detail: "NS-3 for the network, CloudSim Plus for energy, ONNX export and an inference benchmark for deployment." },
      { name: "Calibrated outputs", detail: "Conformal prediction sets, with compression of updates to cut uplink traffic." },
    ],
    decisions: [
      { title: "Patient-level, not record-level", body: "The privacy budget is counted over a patient's roughly eight windows, so the guarantee matches the clinical question." },
      { title: "Head-only differential privacy", body: "The advantage over full-model DP grows as the budget tightens and nearly vanishes without privacy, the pre-registered signature of a noise-tolerance effect." },
      { title: "Every objective reports its own verdict", body: "Results are PASS, INCONCLUSIVE (when a confidence interval spans the target) or NOT MEASURED, so no number is produced from stale data." },
      { title: "Resumable 16-stage pipeline", body: "Completed stages are skipped, and a manifest records the exact command behind every result." },
    ],
    evidence: [
      "VitalDB: 6 of 6 objectives pass. Uplink traffic down 90.8% (target at least 50%), ε = 0.963 (target ≤ 1), P95 latency 2.99 ms (target under 100 ms), update reliability 99.27%, energy saving 72.3% (target at least 20%), accuracy change within the 2-point budget.",
      "WEAR: 5 of 6 objectives pass; the sixth is statistically inconclusive and reported as such.",
      "Multimodal fusion beats both single modalities: +0.0411 AUROC and +0.1062 AUPRC over the best single-modality arm.",
      "Open research data only: the privacy mechanism is demonstrated without any private patient data.",
    ],
    limitations: [
      "Next: pretrain the encoders on an external public corpus to strengthen the main claim.",
      "More seeds will extend confidence intervals beyond the two contrasts that carry them today.",
      "The ASWA component is implemented and will be ablated before it is claimed.",
      "Explainability is currently a calibrated prediction set; feature-level attributions are the next addition.",
    ],
    links: [],
  },
  {
    slug: "cudanet",
    title: "CUDANet",
    domain: "hpc",
    featured: true,
    status: "Working prototype",
    tagline:
      "A from-scratch CUDA MLP engine with correct 2-phase gradient accumulation, SGD/Momentum/Adam, batch norm, LR scheduling, Tensor Core inference and NVML energy profiling.",
    stack: ["CUDA", "C++", "NVML", "Tensor Cores"],
    pipeline: ["Linear", "BatchNorm", "ReLU", "Softmax", "Grad accumulate", "Optimizer"],
    repo: "https://github.com/DEADSERPENT/CUDANet",
    problem:
      "Stateful optimizers such as Momentum and Adam must update their state once per batch, but a naive GPU implementation updates per sample from many threads, which races and silently breaks training. M.Tech High-Performance Computing mini project.",
    approach:
      "Split every step into two phases. Phase 1: each of the nb GPU threads computes its per-sample gradient and adds it into a shared buffer with atomicAdd, touching no optimizer state. Phase 2: one kernel call per batch averages the buffer, applies the Momentum/Adam update and zeroes the buffer. This is race-free at any batch size.",
    architecture: [
      { name: "Network", detail: "Input (784) → [Linear → BatchNorm → ReLU] × nl → Linear → Softmax → Output (10). Default nl=2, nh=128, batch 2048, i.e. 784→128→128→10." },
      { name: "Phase 1: accumulate", detail: "one_learning_cycle (non-BN) and grad_accum_layer_kernel (BN) compute per-sample gradients and atomicAdd them to a shared buffer. No optimizer state is touched." },
      { name: "Phase 2: apply", detail: "apply_optimizer_update runs once per batch: divide by nb, apply Momentum/Adam (bias-corrected), reset the buffer." },
      { name: "Shared-memory tiling", detail: "32×32 tiled shared memory in forward and backward kernels to cut global memory traffic." },
      { name: "LR schedule", detail: "Exponential decay each epoch: lr = lr0 × exp(−0.025 × epoch)." },
    ],
    decisions: [
      { title: "Correctness before speed", body: "Per-sample optimizer updates are fast and wrong. The 2-phase design trades one extra kernel launch per batch for updates that are correct." },
      { title: "Variants as separate binaries", body: "Each optimizer/BN configuration builds to its own binary (cuda, cuda_momentum, cuda_adam, cuda_adam_lr, cuda_batchnorm, cuda_full), which makes the benchmark table reproducible." },
    ],
    evidence: [
      "Peer-reviewed: published in the 2026 International Conference on Natural Language Processing and Computer Vision (ICNPCV), IEEE, as CUDAnet: A Micro-Footprint, Dependency-Free Deep Learning Engine for Edge-Native GPU Training.",
      "Benchmark on an NVIDIA RTX A2000 (sm_86), 20 epochs, 2×128 network, batch 2048: SGD 74.88%, Adam 80.80%, Adam + LR schedule 82.16%, BN + Adam + LR 85.64% test accuracy at ~0.15 s/epoch.",
      "Optimizer dispatch fix: the BN training path had been calling the SGD update whatever optimizer was selected, so with Adam at lr=0.01 the effective step was only ≈ 4.9×10⁻⁶. Dispatching correctly took BN + Adam + LR from 17.91% to 85.64%.",
    ],
    limitations: [
      "Scope: an engine-correctness and performance study of a small MLP, so the focus is correct, fast training rather than leaderboard accuracy.",
      "Benchmarks were run on a single GPU (RTX A2000); running them on your own hardware is the best way to compare.",
    ],
    links: [
      { label: "Paper (IEEE Xplore)", url: "https://ieeexplore.ieee.org/abstract/document/11708751/" },
    ],
  },
  {
    slug: "paysphere",
    title: "PaySphere",
    domain: "backend",
    featured: true,
    status: "Working prototype",
    tagline:
      "A payment-orchestration backend that owns idempotency, an explicit payment state machine, webhook deduplication and reconciliation in front of Razorpay.",
    stack: ["Node.js", "PostgreSQL", "Docker", "Razorpay"],
    pipeline: ["Idempotency-Key", "State machine", "Gateway adapter", "Webhook dedupe", "Reconcile"],
    repo: "https://github.com/DEADSERPENT/PaySphere",
    problem:
      "Callers should get a stable, gateway-agnostic payment API and not talk to the gateway directly, and payments must survive timeouts, crashes, races and duplicate webhooks without double-charging or losing state.",
    approach:
      "Sit between the platform and the gateway. Enforce idempotency on requests, model the payment lifecycle as an explicit state machine, deduplicate webhooks, and run a reconciliation worker loop.",
    architecture: [
      { name: "API", detail: "POST /api/v1/payments with an Idempotency-Key header." },
      { name: "State machine", detail: "Explicit payment lifecycle, documented in docs/payment-lifecycle.md." },
      { name: "Gateway adapter", detail: "GATEWAY_ADAPTER=mock (in-memory, used by tests) or razorpay (real test-mode API)." },
      { name: "Webhooks", detail: "Signatures are verified over the raw request body before any database write; duplicates are deduplicated." },
      { name: "Reconciliation worker", detail: "A separate process (npm run worker) reconciles payment state." },
    ],
    decisions: [
      { title: "Gateway isolation", body: "The mock adapter means the whole system and its tests run without a Razorpay account." },
      { title: "No sensitive card data", body: "Card numbers, CVVs and UPI PINs are never stored, only gateway-issued references." },
    ],
    evidence: [
      "Test suites in the repo: unit, integration (needs a Postgres test DB), failure tests for timeouts, crashes, races and duplicates, and a concurrency load test. docs/failure-modes.md maps each failure scenario to its test.",
    ],
    limitations: [
      "Runs against a mock gateway by default, which makes it easy to try locally. Load-test numbers will be added once they are published.",
    ],
    links: [],
  },
  {
    slug: "grain-regularity",
    short: "Wood Grain",
    title: "Wood Grain Quality Assessment",
    repoName: "Grain-Regularity-Based-Quality-Assessment",
    domain: "vision",
    featured: true,
    status: "Research prototype",
    tagline:
      "A training-free method that grades wood by grain regularity (directional entropy), then combines it with YOLOv8 defect detection and auditable expert rules.",
    stack: ["Python", "Gabor filters", "YOLOv8-nano", "Expert rules"],
    pipeline: ["Gabor bank", "Direction map", "Entropy", "GRS", "Defects", "Rules R1–R4"],
    repo: "https://github.com/DEADSERPENT/Grain-Regularity-Based-Quality-Assessment",
    problem:
      "Wood quality assessment leans on manual inspection or defect detection. Grain regularity and texture uniformity, which decide suitability for carving, furniture or pulping, are rarely measured directly.",
    approach:
      "Measure grain uniformity with signal processing (no labels needed for the core metric), detect defects with a fine-tuned YOLOv8-nano, and map both to a grade through explicit expert rules so every decision can be explained.",
    architecture: [
      { name: "1. Grain direction", detail: "Divide the image into 32×32 patches and extract grain direction with a Gabor filter bank (8 orientations × 3 frequencies). src/grain_direction.py" },
      { name: "2. Directional entropy", detail: "Histogram the directions and compute Shannon entropy (GDE). src/entropy.py" },
      { name: "3. Grain Regularity Score", detail: "GRS = 1 / GDE. Higher means a more uniform grain. Species-agnostic and physically grounded. src/grs.py" },
      { name: "4. Defects + rules", detail: "YOLOv8-nano gives a Defect Density (DD). Rules R1–R4 combine GRS and DD into a grade with an explanation. src/expert_rules.py" },
    ],
    decisions: [
      { title: "Training-free core metric", body: "The quality measurement itself needs no labelled data, so it is not tied to a species or dataset. Only the defect detector is trained." },
      { title: "Rules, not a black box", body: "Grades come from thresholds a wood scientist can read: Premium needs GRS ≥ 1.250, H ≤ 0.80 and DD ≤ 0.10; Standard GRS ≥ 0.625; Processing GRS ≥ 0.417; below that, Reject." },
    ],
    evidence: [],
    limitations: [
      "The method and rule thresholds are documented in full. Running the experiment scripts reproduces the results for your own data.",
    ],
    links: [],
  },
  {
    slug: "drifting-oracle",
    short: "Drifting Oracle",
    title: "Drifting Oracle",
    domain: "ml",
    featured: true,
    status: "Working prototype",
    tagline:
      "A credit-risk monitor that detects drift in its own inputs, retrains against a Champion/Challenger gate, and checks LLM explanations for hallucinated regulatory citations.",
    stack: ["Python", "XGBoost", "MLflow", "Delta Lake", "Ollama"],
    pipeline: ["Bronze", "Silver", "PSI drift", "Retrain", "LLM eval", "Dashboard"],
    repo: "https://github.com/DEADSERPENT/drifting-oracle",
    license: "MIT",
    problem:
      "A credit-scoring model keeps scoring applicants after economic conditions, and therefore feature distributions, have shifted, which calls for continuous monitoring. Separately, an LLM that writes loan-rejection explanations can cite regulations that do not exist, which calls for checking every explanation. Both are continuous-monitoring jobs rather than one-time fixes.",
    approach:
      "Detect distribution shift with the Population Stability Index, retrain a Challenger when drift crosses the threshold, and promote it only if it beats the Champion on a held-out set neither model trained on. In parallel, score generated explanations for factual grounding and citation hallucination against a reference rule list. Runs fully locally on MLflow, Delta Lake and Ollama.",
    architecture: [
      { name: "Medallion data", detail: "Home Credit (350K rows) is the training distribution; German Credit (1K rows, different schema) is scored as an incoming batch with intentionally different distributions, to produce a verifiable drift signal. Raw CSVs land in Bronze and are projected into a shared 10-feature schema in Silver." },
      { name: "Champion model", detail: "An XGBoost model trained on the common schema and registered in the MLflow Model Registry under a champion alias." },
      { name: "PSI drift", detail: "drift/psi.py compares feature distributions between the two sources. Threshold 0.25, the standard credit-risk cutoff from the literature; it is an industry default, not tuned to this data." },
      { name: "Retrain gate", detail: "If PSI is at or above threshold, ml/retrain.py trains a Challenger and compares it to the Champion with mlflow.evaluate on a deterministic, ID-hashed holdout excluded from all training." },
      { name: "LLM evaluation", detail: "Generation (Ollama) and evaluation are decoupled. Two custom metrics, factual grounding and citation hallucination, are deterministic regex/lookup checks against the applicant's own feature values and a reference rule list, not a second LLM call." },
      { name: "Gold tables + dashboard", detail: "Predictions, drift metrics, retraining log, explanations and evaluation scores are written as Delta tables via delta-rs, and read by a dashboard." },
    ],
    decisions: [
      { title: "Deterministic evaluation instead of an LLM judge", body: "Because the metrics are regex/lookup checks, cost and latency per explanation are constant, which is what lets the design scale to a target like 10,000 explanations a day. An LLM-as-judge step would not." },
      { title: "One shared feature schema", body: "The two datasets share no columns, so each feature is a direct match or a documented proxy (for example duration_months is derived as AMT_CREDIT / AMT_ANNUITY on Home Credit). The mapping and rationale live in ml/features.py." },
      { title: "Delta Lake locally in place of Unity Catalog", body: "Real Delta tables (ACID, transaction log, time travel) give the same table guarantees without the catalog layer." },
    ],
    evidence: [
      "Run captured 2026-08-05: Challenger promoted, AUC 0.6942 vs Champion 0.6873; champion alias at registry v11.",
      "Drift on German Credit vs Home Credit: PSI above the 0.25 threshold on 9 of 10 features (payment_burden_pct 8.228, credit_amount 8.135 at the top; own_realty_flag 0.002 the only one with none). German Credit is deliberately out-of-distribution, so this is a test of the detector, not a finding about real credit data.",
    ],
    limitations: [
      "Strengthened the hallucination detector: generic regulatory words in the reference tokens had let fabricated citations pass. After excluding them, two fabricated citations that scored 0 now score 1 and are flagged.",
      "Made the Champion/Challenger comparison leak-proof: about 10.5% of the 'held-out' set had overlapped the Champion's training fold. With a clean holdout the Challenger wins (0.6942 vs 0.6873).",
      "With only 2 of 307,511 applicants rejected after the fix, the LLM-evaluation figures are best read as directional; a larger rejected sample would make them statistically firm.",
      "Next step: add fairness scoring, so generated explanations are checked for protected attributes such as applicant sex.",
    ],
    links: [],
  },
  {
    slug: "resolvr",
    title: "Resolvr",
    domain: "tools",
    featured: true,
    status: "Published",
    tagline:
      "An MCP server that connects a coding agent to the GitHub PR for your current branch, so review comments become verified fixes, with one approval-gated write path.",
    stack: ["Java 21", "Quarkus", "MCP (SSE)", "GitHub REST + GraphQL", "Docker"],
    pipeline: ["Discover PR", "Read threads + CI", "Agent fixes locally", "Human approval", "Commit + push", "Resolve threads"],
    repo: "https://github.com/DEADSERPENT/resolvr",
    license: "MIT",
    problem:
      "GitHub Copilot can review a pull request and leave comments, but it cannot act on them. Someone still has to read every thread, fix the code and resolve each conversation by hand.",
    approach:
      "Resolvr runs no model of its own. It gives an agent you already trust (Copilot Agent Mode in VS Code or JetBrains, or Claude Code) the PR context over the Model Context Protocol, lets the agent fix the code locally, and writes back to GitHub only after an explicit approval.",
    architecture: [
      { name: "Discovery (read-only)", detail: "Workspace → Git remote → owner/repo → current branch → matching open PR, then threads, comments, diff, commits and CI status." },
      { name: "ResolutionService (the only write path)", detail: "prepare_resolution_summary stages an approval package; commit_and_push_resolution is the sole tool that commits or pushes." },
      { name: "CI feedback loop", detail: "A poll-friendly check status tool plus tail-truncated failure-log excerpts, so the agent can re-fix without leaving the editor." },
      { name: "GitHub clients", detail: "GraphQL for review threads with cursor pagination and thread resolution; REST (read-only) for files, checks and diffs; shared retry and rate-limit handling." },
    ],
    decisions: [
      { title: "One approval-gated write path", body: "Every other tool is read-only, and resolving threads only works after a push has actually succeeded." },
      { title: "Trust, but verify", body: "Before writing anything it re-checks branch, HEAD, working tree and PR state against Git and GitHub rather than trusting what the agent says it changed." },
      { title: "Fail closed", body: "A packaged instance refuses to start without an API key, and every route except the health check requires it." },
      { title: "Agent-driven, no background work", body: "No webhooks, queues or server-side polling. Resolvr only acts when the agent asks." },
    ],
    evidence: [
      "Shipped as Resolvr CLI 1.0.0 with native installers for Windows, macOS (Intel and Apple Silicon) and Linux, each bundling its own JVM, or self-hosted with Docker.",
      "Built and tested in CI on GitHub Actions, and released under the MIT licence.",
    ],
    limitations: [
      "Designed for self-hosting by a team with one shared GitHub token; a GitHub App with per-installation tokens is the route to a hosted service.",
      "Prepared resolutions are held in memory, so a restart simply starts a fresh approval; a persistent store would extend this to multiple replicas.",
      "CI failure logs cover checks created by GitHub Actions, and excerpts show the tail of the log, where failures usually appear, with a link to the full log.",
    ],
    links: [
      { label: "Latest release", url: "https://github.com/DEADSERPENT/resolvr/releases/latest" },
    ],
  },
  {
    slug: "federated-traffic",
    short: "Federated traffic",
    title: "Federated Traffic Signal Control",
    repoName: "federated-traffic-signal-control",
    domain: "privacy",
    status: "Ongoing research",
    tagline:
      "Adaptive traffic-signal control where intersections learn together with federated learning and share no raw traffic data, tested under simulated V2I network stress.",
    stack: ["Python", "FedAvg", "CloudSim", "NS-3 (802.11p)", "ZeroMQ"],
    pipeline: ["Local training", "FedAvg aggregation", "Adaptive control", "Network stress test"],
    repo: "https://github.com/DEADSERPENT/federated-traffic-signal-control",
    problem:
      "Traffic signals work better when they learn from many intersections, but pooling raw traffic data raises privacy concerns and depends on a network that is rarely ideal.",
    approach:
      "Each intersection trains locally and only model updates are averaged (FedAvg). CloudSim models the edge and cloud compute, and NS-3 simulates 802.11p DSRC vehicle-to-infrastructure links, so the controller is evaluated under realistic latency and packet loss.",
    architecture: [
      { name: "Federated controller", detail: "A deep-network adaptive controller with weighted FedAvg aggregation and real-time signal control." },
      { name: "CloudSim", detail: "Edge servers, cloud VMs, task scheduling and resource management for the training workload." },
      { name: "NS-3", detail: "802.11p DSRC V2I communication with latency, packet-loss and bandwidth modelling." },
      { name: "Bridge", detail: "A ZeroMQ link between the learning controller and the simulators." },
    ],
    decisions: [
      { title: "Three simulators, one study", body: "Learning, compute and network are modelled together, so results reflect the network the controller would actually face." },
      { title: "Statistics over single runs", body: "Reported figures are means with standard deviations over five runs." },
    ],
    evidence: [
      "Mean wait time over 5 runs: Fixed-time 13.23 ± 0.31 s, local-only learning 9.13 ± 0.22 s, federated 9.06 ± 0.18 s.",
      "Federated learning kept the best prediction error in all five network scenarios, from an ideal link (29 ms, 0% loss) to an extreme one (658 ms, 20% loss).",
      "No raw traffic data is shared between intersections.",
    ],
    limitations: [
      "Results come from simulation; a field trial is the natural next step.",
      "The wait-time gap between federated and local-only learning is within run-to-run variation, so the strongest case for federation here is privacy plus robustness.",
    ],
    links: [],
  },
  {
    slug: "debtsense",
    short: "DebtSense",
    title: "DebtSense",
    domain: "tools",
    status: "Core complete",
    tagline:
      "A software health platform that predicts which files are the highest maintenance risk before they become production incidents, with every score explained.",
    stack: ["Python", "NetworkX", "FastAPI", "Git history", "Plugin architecture"],
    pipeline: ["Snapshot repo", "Static metrics", "Git evolution", "Knowledge graph", "Explainable risk"],
    repo: "https://github.com/DEADSERPENT/DebtSense",
    problem:
      "Most AI-for-code tools help write code. Engineering managers ask a different question: which modules should be refactored before more is built on top of them?",
    approach:
      "Combine static code metrics, Git change history and a repository knowledge graph into explainable software-health gauges. Every score traces back to a Rule → Evaluation → Finding chain with a plain-language reason and the evidence behind it.",
    architecture: [
      { name: "Analysis engine", detail: "Framework-free engine with plugin-based metrics; the FastAPI service and the CLI are interchangeable front ends." },
      { name: "Repository graph", detail: "A NetworkX knowledge graph finds cyclic dependencies, hub modules and Stable Dependencies Principle violations." },
      { name: "Snapshots", detail: "Each analysis records the commit hash and per-file content hashes, so re-running a commit reproduces the same report." },
      { name: "Registries", detail: "Metrics, rules and the predictor are registries, so a trained model can replace the weighted formula without changing the pipeline." },
    ],
    decisions: [
      { title: "Explainable, not a black box", body: "A risk score always arrives with its reason and the exact evidence that triggered it." },
      { title: "Architecture-aware", body: "It looks beyond per-file length to how modules depend on each other." },
      { title: "Engine first", body: "A VS Code extension or GitHub Action can become another front end without touching the engine." },
    ],
    evidence: [
      "The core analysis engine is complete, tested and frozen at v1.0.",
      "Deterministic by design: reports are reproducible against the same commit.",
    ],
    limitations: [
      "Next: grounded LLM explanations, an interactive dashboard, PDF reports, a VS Code extension and ML-based prediction, all designed as extension points.",
    ],
    links: [],
  },
  {
    slug: "digipin",
    short: "digipin",
    title: "digipin",
    domain: "tools",
    status: "Published",
    tagline:
      "Zero-dependency Python and JavaScript libraries for India's DIGIPIN national addressing grid: coordinates to a short code accurate to about 4 m, with no API required.",
    stack: ["Python", "JavaScript / TypeScript", "Zero dependencies", "PyPI", "npm"],
    pipeline: ["Coordinates", "Grid cell", "10-character code", "Decode back"],
    repo: "https://github.com/DEADSERPENT/digipin",
    license: "MIT",
    problem:
      "DIGIPIN, the Department of Posts' geocoding standard, divides the country into a hierarchical grid with a unique code for every roughly 4 m × 4 m location. Developers need a small, correct, dependency-free implementation they can drop into any stack.",
    approach:
      "Two reference implementations of the same specification, one for Python and one for JavaScript and TypeScript, with matching encode and decode behaviour and integrations for common frameworks.",
    architecture: [
      { name: "digipinpy (Python)", detail: "Pure Python with integrations for Pandas, Django, FastAPI and Flask, plus a CLI for batch CSV and Excel processing." },
      { name: "digipinjs-lib (JavaScript)", detail: "Full TypeScript definitions, usable in Node.js and the browser." },
      { name: "Precision levels", detail: "From country and state scale down to doorstep accuracy, selectable per call." },
    ],
    decisions: [
      { title: "No dependencies", body: "Nothing to install beyond the package itself, which keeps it safe to embed." },
      { title: "One spec, two languages", body: "Both implementations target full specification compliance, so codes agree across stacks." },
    ],
    evidence: [
      "Published on PyPI (digipinpy) and npm (digipinjs-lib), with a Zenodo DOI for citation.",
      "About 50,000 encodes per second in Python; under 5 KB gzipped in JavaScript.",
      "Example: encode(28.622788, 77.213033) gives 39J49LL8T4, and decode returns the cell.",
    ],
    limitations: [],
    links: [
      { label: "PyPI", url: "https://pypi.org/project/digipinpy/" },
      { label: "npm", url: "https://www.npmjs.com/package/digipinjs-lib" },
      { label: "DOI", url: "https://doi.org/10.5281/zenodo.17916240" },
    ],
  },
  {
    slug: "dropout-signal",
    short: "Dropout Signal",
    title: "The Dropout Signal",
    repoName: "The-Dropout-Signal",
    domain: "ml",
    status: "Working prototype",
    tagline:
      "An end-to-end MLOps pipeline that predicts student dropout risk, explains each prediction with SHAP, audits fairness, and turns scores into intervention-ready outputs.",
    stack: ["Python", "XGBoost", "MLflow", "Delta Lake", "FastAPI", "React"],
    pipeline: ["Bronze", "Silver", "Train + MLflow", "SHAP + fairness", "Gold", "Dashboard"],
    repo: "https://github.com/DEADSERPENT/The-Dropout-Signal",
    license: "MIT",
    problem:
      "Universities need to know which students are at risk early enough to help, and they need the reasons and a fairness check, not only a score.",
    approach:
      "A medallion architecture turns raw records into one scored row per student with a risk tier, a recommended action and the top three SHAP risk factors. Models are tracked and registered in MLflow, and a dashboard reads live from the pipeline's own outputs.",
    architecture: [
      { name: "Bronze", detail: "Raw ingestion with column normalisation only, no business logic." },
      { name: "Silver", detail: "Null cleaning and feature engineering: grade delta, absenteeism trend, financial stress index." },
      { name: "Gold", detail: "One row per scored student: risk score, risk tier, recommended action and the top three SHAP factors." },
      { name: "Two implementations", detail: "A local pipeline (Delta Lake, pandas, scikit-learn, XGBoost, MLflow) and a PySpark and Unity Catalog mirror validated on a live Databricks cluster." },
      { name: "Dashboard", detail: "FastAPI backend and React frontend showing risk, SHAP explanations, fairness metrics and recommended interventions." },
    ],
    decisions: [
      { title: "Explain every prediction", body: "SHAP factors travel with each score into the Gold table." },
      { title: "Audit fairness, then try to improve it", body: "A fairness audit across gender and socioeconomic group is followed by a mitigation pass that reports both successful and unsuccessful outcomes." },
      { title: "Reproducible by construction", body: "A fixed stratified split (random state 42), so every reported metric can be regenerated by rerunning the pipeline." },
    ],
    evidence: [
      "Champion model: XGBoost with ROC-AUC 0.939 against 0.928 for the logistic-regression baseline, registered in the MLflow Model Registry with the alias champion.",
      "885 students scored: 251 high risk, 117 medium, 517 low. 85.7% of the high tier actually dropped out.",
      "Extended validation on the Open University clickstream data (10.6 million VLE log rows): ROC-AUC 0.943, so the approach carries to a second, independent dataset.",
    ],
    limitations: [
      "Next: equal-opportunity mitigation beyond per-group thresholds, to narrow the gaps the audit surfaces.",
    ],
    links: [],
  },
];

export const statusTone = {
  Published: "ok",
  "Core complete": "info",
  "Working prototype": "warn",
  "Research prototype": "warn",
  "Ongoing research": "info",
};

export const asOf = "2026-10-23";

/**
 * Charts shown in case files. Values are copied from the READMEs (see captions).
 * `max` fixes the axis; `threshold` draws a reference line.
 */
export const charts = {
  "drifting-oracle": {
    title: "PSI per feature: German Credit vs Home Credit",
    unit: "PSI",
    threshold: { value: 0.25, label: "retrain threshold 0.25" },
    caption:
      "From the run on 2026-08-05. German Credit is deliberately out-of-distribution, so this tests the detector, not real credit data. Bars are linear: own_realty_flag (0.002) is the only feature below the threshold.",
    bars: [
      { label: "payment_burden_pct", value: 8.228 },
      { label: "credit_amount", value: 8.135 },
      { label: "num_dependents", value: 7.243 },
      { label: "residence_stability_years", value: 7.195 },
      { label: "employment_years", value: 4.733 },
      { label: "existing_credit_lines", value: 3.612 },
      { label: "duration_months", value: 0.914 },
      { label: "age_years", value: 0.582 },
      { label: "is_male", value: 0.507 },
      { label: "own_realty_flag", value: 0.002 },
    ],
  },
  "dropout-signal": {
    title: "Scored students by risk tier (885 on the held-out test set)",
    unit: "students",
    caption: "85.7% of the high-risk tier actually dropped out, showing strong separation between high- and low-risk students.",
    bars: [
      { label: "High risk", value: 251, highlight: true },
      { label: "Medium risk", value: 117 },
      { label: "Low risk", value: 517 },
    ],
  },
  "pp-hfm": {
    title: "Head-only DP advantage over full-model DP, by privacy budget (VitalDB)",
    unit: "gap",
    caption:
      "The advantage is largest when the privacy budget is tightest (ε = 0.5) and nearly vanishes without privacy (ε = ∞), which points to a noise-tolerance effect rather than a parameter-count effect.",
    bars: [
      { label: "ε = 0.5", value: 0.131, highlight: true },
      { label: "ε = 1", value: 0.113 },
      { label: "ε = 2", value: 0.097 },
      { label: "ε = 4", value: 0.078 },
      { label: "ε = 8", value: 0.06 },
      { label: "ε = ∞ (no privacy)", value: 0.009 },
    ],
  },
  "federated-traffic": {
    title: "Mean vehicle wait time by controller (lower is better)",
    unit: "seconds",
    caption: "Mean of 5 runs in simulation. Federated learning shares only model updates, never raw traffic data.",
    bars: [
      { label: "Fixed-time", value: 13.23 },
      { label: "Local-only learning", value: 9.13 },
      { label: "Federated learning", value: 9.06, highlight: true },
    ],
  },
  cudanet: {
    title: "Test accuracy by configuration",
    unit: "%",
    caption:
      "NVIDIA RTX A2000 (sm_86), 20 epochs, 2×128 network, batch 2048. Single-GPU figures; re-run on your own hardware.",
    bars: [
      { label: "SGD", value: 74.88 },
      { label: "Momentum", value: 74.65 },
      { label: "Adam", value: 80.8 },
      { label: "Adam + LR schedule", value: 82.16 },
      { label: "BatchNorm + SGD", value: 60.68 },
      { label: "BatchNorm + Adam + LR", value: 85.64, highlight: true },
    ],
  },
};
